import _ from 'lodash';
import * as types from '../constants/ActionTypes';
import client from '../lib/frost-web-client';
import logout from '../lib/logout';

let xmlWorkers = {};
let zipWorker = null;

export function clearFeedError() {
	return { type: types.CLEAR_FEED_ERROR };
}

export function submitAddFeed() {
	return { type: types.SUBMIT_ADD_FEED };
}

export function resolveAddFeed(feed) {
	return { type: types.RESOLVE_ADD_FEED, feed };
}

export function resolveAddFeedError(response) {
	return { type: types.RESOLVE_ADD_FEED_ERROR, error: response.body };
}

export function addFeed(feed) {
	return dispatch => {
		dispatch(submitAddFeed());

		return client
			.post('/api/feeds', feed)
			.then(response => {
				// Set the new feed ID
				feed.id = response.body.id;

				dispatch(resolveAddFeed(feed));
			})
			.catch(error => {
				if (!logout(error, dispatch)) {
					dispatch(resolveAddFeedError(error.response));
				}
			});
	};
}

export function submitEditFeed() {
	return { type: types.SUBMIT_EDIT_FEED };
}

export function resolveEditFeed(feed) {
	return { type: types.RESOLVE_EDIT_FEED, feed };
}

export function resolveEditFeedError(response) {
	return { type: types.RESOLVE_EDIT_FEED_ERROR, error: response.body };
}

export function editFeed(feed) {
	return dispatch => {
		dispatch(submitEditFeed());

		return client
			.put(`/api/feeds/${feed.id}`, feed)
			.then(dispatch(resolveEditFeed(feed)))
			.catch(error => {
				if (!logout(error, dispatch)) {
					dispatch(resolveEditFeedError(error.response));
				}
			});
	};
}

export function submitForceFeed(feed) {
	return { type: types.SUBMIT_FORCE_FEED, feed };
}

export function resolveForceFeed(feed) {
	return { type: types.RESOLVE_FORCE_FEED, feed };
}

export function resolveForceFeedError(response) {
	return { type: types.RESOLVE_FORCE_FEED_ERROR, error: response.message };
}

export function forceFeed(feed) {
	return dispatch => {
		dispatch(submitForceFeed(feed));

		return client
			.put(`/api/feeds/${feed.id}/force-run`, { verificationHash: null })
			.then(dispatch(resolveForceFeed(feed)))
			.catch(error => {
				if (!logout(error, dispatch)) {
					dispatch(resolveForceFeedError(error));
				}
			});
	};
}

export function submitFetchFeedXml() {
	return { type: types.SUBMIT_FETCH_FEED_XML };
}

export function resolveFetchFeedXml() {
	return { type: types.RESOLVE_FETCH_FEED_XML };
}

export function resolveFetchFeedXmlError(error) {
	return { type: types.RESOLVE_FETCH_FEED_XML_ERROR, error };
}

export function fetchFeedXml(requestId, feed) {
	return dispatch => {
		dispatch(submitFetchFeedXml());

		return client
			.put(`/api/feed-streams/${requestId}`, { source: feed.source })
			.responseType(feed.downloadType === 'zip' ? 'arraybuffer' : 'text')
			.then(response => {
				if (feed.downloadType === 'zip') {
					dispatch(resolveFetchFeedXml());
					dispatch(extractFeedXml(response.xhr.response));
				} else if (!response.body || !response.body.cancelled) {
					dispatch(resolveFetchFeedXml());
					dispatch(parseFeedXml(feed.name, response.text));
				}
			})
			.catch(error => {
				if (!logout(error, dispatch) && error.response.text !== 'Canceled request') {
					dispatch(resolveFetchFeedXmlError(new Error('XML Request Error')));
				}
			});
	};
}

export function submitExtractFeedXml() {
	return { type: types.SUBMIT_EXTRACT_FEED_XML };
}

export function resolveExtractFeedXml() {
	return { type: types.RESOLVE_EXTRACT_FEED_XML };
}

export function resolveExtractFeedXmlError(error) {
	return { type: types.RESOLVE_EXTRACT_FEED_XML_ERROR, error };
}

export function extractFeedXml(zip) {
	return dispatch => {
		dispatch(submitExtractFeedXml());

		zipWorker = new Worker('assets/js/workers/zip.worker.js');
		zipWorker.onmessage = event => {
			const content = event.data;
			switch (content.type) {
				case 'error':
					dispatch(
						resolveExtractFeedXmlError(new Error('Unable to extract the zip file')),
					);
					zipWorker.terminate();
					zipWorker = null;
					break;
				case 'data':
					dispatch(parseFeedXml(content.message.fileName, content.message.xml, true));
					break;
				case 'done':
					dispatch(resolveExtractFeedXml());
					zipWorker.terminate();
					zipWorker = null;
					break;
			}
		};

		dispatch(submitExtractFeedXml());
		zipWorker.postMessage(new Uint8Array(zip));
	};
}

export function receiveParseFeedXmlWarning(name, warning) {
	return { type: types.RECEIVE_PARSE_FEED_XML_WARNING, name, warning };
}

export function clearParseFeedXmlWarning(warningId) {
	return { type: types.CLEAR_PARSE_FEED_XML_WARNING, warningId };
}

export function submitParseFeedXml(name) {
	return { type: types.SUBMIT_PARSE_FEED_XML, name };
}

export function resolveParseFeedXml(name, data) {
	return { type: types.RESOLVE_PARSE_FEED_XML, name, data };
}

export function resolveParseFeedXmlError(name, error) {
	return { type: types.RESOLVE_PARSE_FEED_XML_ERROR, name, error };
}

export function parseFeedXml(fileName, xml, isWarningEnabled = false) {
	return dispatch => {
		dispatch(submitParseFeedXml(fileName));

		const xmlWorker = new Worker('assets/js/workers/xml.worker.js');
		xmlWorkers[fileName] = xmlWorker;

		xmlWorker.onmessage = event => {
			const content = event.data;
			switch (content.type) {
				case 'error':
					dispatch(resolveParseFeedXmlError(fileName, new Error(content.message)));
					break;
				case 'warning':
					dispatch(
						receiveParseFeedXmlWarning(fileName, {
							id: Math.random(),
							message: content.message,
						}),
					);
					break;
				case 'data':
					dispatch(resolveParseFeedXml(fileName, content.message));
					break;
			}
			xmlWorkers[fileName].terminate();
			delete xmlWorkers[fileName];
		};

		xmlWorker.postMessage({ fileName, xml, isWarningEnabled });
	};
}

export function resolveCancelFeedLoad() {
	return { type: types.RESOLVE_CANCEL_FEED_LOAD };
}

export function cancelFeedLoad(requestId) {
	return dispatch => {
		if (Object.keys(xmlWorkers).length) {
			// Terminate all running xml workers and clear state
			_.each(xmlWorkers, worker => worker.terminate());
			xmlWorkers = {};
			dispatch(clearFeedXmlState());
		} else if (zipWorker) {
			// Terminate the running zip worker and clear state
			zipWorker.terminate();
			zipWorker = null;
			dispatch(clearFeedXmlState());
		} else {
			// Clear state and cancel the feed fetch
			dispatch(clearFeedXmlState());
			return client
				.del(`/api/feed-streams/${requestId}`)
				.then(() => dispatch(resolveCancelFeedLoad()))
				.catch(err => console.error(err));
		}
	};
}

export function clearFeedXmlState() {
	return { type: types.CLEAR_FEED_XML_STATE };
}



// WEBPACK FOOTER //
// ./src/assets/js/actions/feeds.js